import React, { useState, useEffect, useMemo } from 'react';
import {
  FiClipboard,
  FiCheckCircle,
  FiTrendingUp,
  FiAlertTriangle,
  FiActivity,
  FiEdit,
  FiRefreshCw,
  FiList,
  FiLoader,
} from 'react-icons/fi';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  BarChart,
  Bar,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { assessmentsApi } from '../../services/api';
import { useAccessCheck } from '../../hooks/useAccessCheck';

// Static data for User Dashboard

const CARDS = [
  {
    title: 'Total Assessments',
    value: 24,
    icon: FiClipboard,
    bg: 'bg-[#0077b5]',
    change: '+3 this month',
  },
  {
    title: 'Completed',
    value: 16,
    icon: FiCheckCircle,
    bg: 'bg-[#0077b5]',
    change: '67% completion rate',
  },
  {
    title: 'Avg Risk Score',
    value: 3.8,
    icon: FiTrendingUp,
    bg: 'bg-[#F47F2E]',
    change: 'Out of 5.0',
  },
  {
    title: 'High Risk Items',
    value: 5,
    icon: FiAlertTriangle,
    bg: 'bg-[#F47F2E]',
    change: 'Needs attention',
  },
];

const riskPieData = [
  { name: 'Critical (5)', value: 2, color: '#F47F2E' },
  { name: 'High (4)', value: 3, color: '#53545a' },
  { name: 'Medium (3)', value: 8, color: '#919191' },
  { name: 'Low (2)', value: 7, color: '#0077b5' },
  { name: 'Very Low (1)', value: 4, color: '#0077b5' },
];

// monthlyCompletionData computed from API-provided assessment.completedAt
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// will be populated in component via useMemo using dashboardAssessments

const frameworkBarData = [
  { framework: 'NIST 800-53', count: 6 },
  { framework: 'SIG Lite', count: 8 },
  { framework: 'ISO 27001', count: 4 },
  { framework: 'SOC 2', count: 3 },
  { framework: 'GDPR', count: 2 },
  { framework: 'HIPAA', count: 1 },
];

const recentActivity = [
  { id: 1, action: 'Completed', entity: 'Bitsight - NIST 800-53 2024', updatedBy: 'Rajesh Haridas', time: '2 hours ago', type: 'completed' },
  { id: 2, action: 'Updated', entity: 'Ubiquiti - NIST 800-53 2024', updatedBy: 'Britto Chako', time: '5 hours ago', type: 'updated' },
  { id: 3, action: 'Completed', entity: 'Valorant - SIG Lite', updatedBy: 'Ravi Anadhanam', time: '1 day ago', type: 'completed' },
  { id: 4, action: 'Updated', entity: 'Employee Bridge - NIST 800', updatedBy: 'Rajesh Haridas', time: '1 day ago', type: 'updated' },
  { id: 5, action: 'Completed', entity: 'Exbeam - NIST 800-53 2024', updatedBy: 'Rajesh Haridas', time: '2 days ago', type: 'completed' },
  { id: 6, action: 'Updated', entity: 'Zenco Engineering - ISO 27001', updatedBy: 'Amit Dutta', time: '3 days ago', type: 'updated' },
];

const UserDashboard = () => {
  const { user } = useAuth();
  const [dashboardAssessments, setDashboardAssessments] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);

    useAccessCheck('cud_dashboard', '/user/assessments');
  
  useEffect(() => {
    if (!user?.id) return;
    const fetchDashboard = async (useDebug = false) => {
      setDashboardLoading(true);
      setDashboardError(null);
      try {
        const data = await assessmentsApi.CountInfo(user.id);
        setDashboardAssessments(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load dashboard assessments:', err);
        setDashboardError('Failed to load your assessments.');
      } finally {
        setDashboardLoading(false);
      }
    };

    fetchDashboard();
  }, [user?.id]);

  // Derived summary values
  const totalAssessments = Array.isArray(dashboardAssessments) ? dashboardAssessments.length : 0;
  const completedAssessments = Array.isArray(dashboardAssessments)
    ? dashboardAssessments.filter(a => {
        // An assessment is considered completed when it has at least one questionnaire and
        // every questionnaire has answered >= totalQuestion
        if (!Array.isArray(a.questionnaires) || a.questionnaires.length === 0) return false;
        return a.questionnaires.every(q => (q.answered ?? 0) >= (q.totalQuestion ?? 0));
      }).length
    : 0;
  const completionRate = totalAssessments > 0 ? `${Math.round((completedAssessments / totalAssessments) * 100)}%` : '0%';

  // Risk score aggregates (avg, high risk count, distribution for pie)
  const { avgRiskScore, highRiskItems, riskPieDataDynamic } = useMemo(() => {
    if (!Array.isArray(dashboardAssessments) || dashboardAssessments.length === 0) {
      return {
        avgRiskScore: 0,
        highRiskItems: 0,
        riskPieDataDynamic: [
          { name: 'Critical (5)', value: 0, color: '#F47F2E' },
          { name: 'High (4)', value: 0, color: '#53545a' },
          { name: 'Medium (3)', value: 0, color: '#919191' },
          { name: 'Low (2)', value: 0, color: '#0077b5' },
          { name: 'Very Low (1)', value: 0, color: '#0077b5' },
        ],
      };
    }

    const buckets = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let sum = 0;
    let count = 0;
    dashboardAssessments.forEach(a => {
      const s = typeof a.riskScore === 'number' ? a.riskScore : null;
      if (s && s >= 1 && s <= 5) {
        buckets[s] = (buckets[s] || 0) + 1;
        sum += s;
        count += 1;
      }
    });

    const avg = count > 0 ? Math.round((sum / count) * 10) / 10 : 0;
    const high = (buckets[5] || 0) + (buckets[4] || 0);

    const pie = [
      { name: 'Critical', value: buckets[5] || 0, color: '#F47F2E' },
      { name: 'High', value: buckets[4] || 0, color: '#53545a' },
      { name: 'Medium', value: buckets[3] || 0, color: '#919191' },
      { name: 'Low', value: buckets[2] || 0, color: '#0077b5' },
      { name: 'Very Low', value: buckets[1] || 0, color: '#0077b5' },
    ];

    return { avgRiskScore: avg, highRiskItems: high, riskPieDataDynamic: pie };
  }, [dashboardAssessments]);

  // Build monthly completion data (last 8 months) by deriving completion date from
  // questionnaires[].lastUpdated when an assessment is considered completed.
  // An assessment is considered completed when it has at least one questionnaire and
  // every questionnaire has answered >= totalQuestion.
  const monthlyCompletionData = useMemo(() => {
    const monthsToShow = 8;
    const now = new Date();
    const window = [];
    for (let i = monthsToShow - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      window.push({ key: `${d.getFullYear()}-${d.getMonth()}`, month: MONTHS[d.getMonth()], completed: 0 });
    }

    if (!Array.isArray(dashboardAssessments) || dashboardAssessments.length === 0) return window.map(w => ({ month: w.month, completed: 0 }));

    const counts = {};

    dashboardAssessments.forEach(a => {
      if (!Array.isArray(a.questionnaires) || a.questionnaires.length === 0) return;

      // determine if assessment is completed (all questionnaires fully answered)
      const isCompleted = a.questionnaires.every(q => (q.answered ?? 0) >= (q.totalQuestion ?? 0));
      if (!isCompleted) return;

      // derive completion date as the latest lastUpdated among questionnaires
      let latest = null;
      a.questionnaires.forEach(q => {
        if (!q?.lastUpdated) return;
        const d = new Date(q.lastUpdated);
        if (isNaN(d)) return;
        if (!latest || d > latest) latest = d;
      });
      if (!latest) return;

      const key = `${latest.getFullYear()}-${latest.getMonth()}`;
      counts[key] = (counts[key] || 0) + 1;
    });

    return window.map(w => ({ month: w.month, completed: counts[w.key] || 0 }));
  }, [dashboardAssessments]);

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>

        <p className={`text-sm mt-1 text-[#919191]`}>
          Overview of your assessment portfolio and risk metrics
        </p>
      </div>

      {/* 4 Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {dashboardLoading ? (
          // show 4 skeleton cards while CountInfo loads
          CARDS.map((card) => (
            <div key={card.title} className={`rounded-2xl p-6 bg-white border border-[#919191]/30 shadow-sm`}>
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gray-200 animate-pulse`} />
              </div>
              <div className="h-8 w-3/4 bg-gray-200 animate-pulse rounded mb-2" />
              <div className="h-4 w-1/3 bg-gray-200 animate-pulse rounded" />
            </div>
          ))
        ) : (
          CARDS.map((card) => {
            const IconEl = card.icon;
            return (
              <div
                key={card.title}
                className={`rounded-2xl p-6 ${
                  'bg-[#ffffff] border border-[#919191]/30 shadow-sm'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl ${card.bg} flex items-center justify-center`}>
                    <IconEl className="text-[#ffffff] text-xl" />
                  </div>
                </div>
                <p className={`text-3xl font-bold text-[#53545a]`}>{card.title === 'Total Assessments' ? totalAssessments : card.title === 'Completed' ? completedAssessments : card.title === 'Avg Risk Score' ? avgRiskScore : card.title === 'High Risk Items' ? highRiskItems : card.value}</p>
                <p className={`text-sm font-medium mt-1 text-[#53545a]`}>{card.title}</p>
              </div>
            );
          })
        )}
      </div>

      {/* Charts Row 1: Pie Chart + Monthly Completion */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Risk Score Pie Chart */}
        <div className={`rounded-2xl p-6 bg-[#ffffff] border border-[#919191]/30 shadow-sm`}>
          <div className="mb-4">
            <h2 className={`text-lg font-semibold text-[#53545a]`}>Risk Score Distribution</h2>
            <p className={`text-xs mt-1 text-[#919191]`}>Assessments by risk level</p>
          </div>
          {dashboardLoading ? (
            <div className="w-full h-[300px] flex items-center justify-center">
              <div className="w-full h-full rounded-xl bg-white border border-[#e6eef5] p-6">
                <div className="flex items-center justify-center h-full">
                  <div className="relative w-56 h-56">
                    <div className="w-56 h-56 rounded-full bg-[#e6eef5] animate-pulse" />
                    <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-28 h-28 bg-white rounded-full" />
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-center gap-6">
                  <div className="w-20 h-3 bg-[#e6eef5] rounded animate-pulse" />
                  <div className="w-20 h-3 bg-[#e6eef5] rounded animate-pulse" />
                  <div className="w-20 h-3 bg-[#e6eef5] rounded animate-pulse" />
                </div>
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                {(() => {
                  // filter out zero-value segments so they are not rendered or shown in legend
                  const pieDataFiltered = (riskPieDataDynamic || []).filter(e => (e.value ?? 0) > 0);
                  return (
                    <>
                      <Pie
                        data={pieDataFiltered}
                        cx="50%"
                        cy="45%"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={4}
                        dataKey="value"
                        stroke="none"
                        tabIndex={-1}
                        onMouseDown={() => {}}
                        onFocus={() => {}}
                        onBlur={() => {}}
                        style={{ outline: 'none' }}
                        label={({ name, percent }) => {
                          if (!percent || percent === 0) return null;
                          return `${name} ${(percent * 100).toFixed(0)}%`;
                        }}
                      >
                        {pieDataFiltered.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} stroke="none" tabIndex={-1} style={{ outline: 'none' }} />
                        ))}
                      </Pie>
                      {/* <Legend verticalAlign="bottom" height={36} payload={pieDataFiltered.map(p => ({ value: p.value, payload: p, color: p.color, id: p.name, dataKey: 'value', name: p.name }))} wrapperStyle={{ outline: 'none' }} /> */}
                      <Tooltip />
                    </>
                  );
                })()}
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Monthly Completion Graph */}
        <div className={`rounded-2xl p-6 bg-[#ffffff] border border-[#919191]/30 shadow-sm`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className={`text-lg font-semibold text-[#53545a]`}>Monthly Completions</h2>
              <p className={`text-xs mt-1 text-[#919191]`}>Assessments completed per month</p>
            </div>
            <FiActivity className={`text-lg text-[#919191]`} />
          </div>
          {dashboardLoading ? (
            <div className="w-full h-[300px] rounded-xl bg-white border border-[#e6eef5] p-4">
              <div className="relative w-full h-full">
                {/* Y-axis ticks */}
                <div className="absolute left-0 top-0 bottom-8 w-10 flex flex-col justify-between py-4">
                  {[0,1,2,3,4].map(i => (
                    <div key={i} className="w-6 h-3 bg-[#e6eef5] rounded animate-pulse" />
                  ))}
                </div>

                {/* Grid and area skeleton */}
                <div className="absolute left-10 right-0 top-0 bottom-8">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="absolute left-0 right-0" style={{ top: `${(i / 5) * 100}%` }}>
                      <div className="h-px bg-[#e6eef5]" />
                    </div>
                  ))}

                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <svg viewBox="0 0 700 200" preserveAspectRatio="none" className="w-full h-full">
                      <defs>
                        <linearGradient id="userSkeletonArea" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0%" stopColor="#e6eef5" stopOpacity="0.95" />
                          <stop offset="100%" stopColor="#e6eef5" stopOpacity="0.45" />
                        </linearGradient>
                      </defs>
                      <path d="M0,140 C80,120 160,110 240,100 C320,95 400,80 480,70 C560,60 640,50 700,55 L700,200 L0,200 Z"
                        fill="url(#userSkeletonArea)" stroke="#e6eef5" strokeWidth="2" className="animate-pulse" />
                      <path d="M0,140 C80,120 160,110 240,100 C320,95 400,80 480,70 C560,60 640,50 700,55"
                        fill="none" stroke="#d1e6f3" strokeWidth="2" className="animate-pulse" />
                    </svg>
                  </div>

                  <div className="absolute left-2 right-2 bottom-0 flex items-center justify-between mt-2">
                    {Array.from({ length: 8 }).map((_, idx) => (
                      <div key={idx} className="w-8 h-3 bg-[#e6eef5] rounded animate-pulse" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={monthlyCompletionData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="colorComp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0077b5" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#0077b5" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#919191" strokeOpacity={0.2} />
                <XAxis dataKey="month" stroke="#919191" tick={{ fontSize: 12 }} />
                <YAxis stroke="#919191" tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: `1px solid #919191`,
                    borderRadius: '12px',
                    color: '#53545a',
                  }}
                />
                <Area type="monotone" dataKey="completed" stroke="#0077b5" fill="url(#colorComp)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

    
      {/* <div className={`rounded-2xl p-6 bg-[#ffffff] border border-[#919191]/30 shadow-sm`}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className={`text-lg font-semibold text-[#53545a]`}>Assessments by Framework</h2>
            <p className={`text-xs mt-1 text-[#919191]`}>Distribution across compliance frameworks</p>
          </div>
        </div>
        {dashboardLoading ? (
          <div className="w-full h-[300px]"><div className="w-full h-full bg-gray-100 rounded-xl animate-pulse" /></div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={frameworkBarData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#919191" strokeOpacity={0.2} />
              <XAxis dataKey="framework" stroke="#919191" tick={{ fontSize: 11 }} />
              <YAxis stroke="#919191" tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: `1px solid #919191`,
                  borderRadius: '12px',
                  color: '#53545a',
                }}
              />
              <Bar dataKey="count" fill="#0077b5" radius={[6, 6, 0, 0]} barSize={40} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div> */}

    

    
    </div>
  );
};

export default UserDashboard;
